export type RiskArticle = {
  title: string;
  /** Slug of a published article. */
  slug: string;
};

export type RiskCategory = {
  id: string;
  no: string;
  title: string;
  intro: string;
  articles: RiskArticle[];
  /** Grid width on the 12-column desktop editorial grid. */
  colSpan: 4 | 5 | 6 | 7 | 12;
  tone: 'primary' | 'accent' | 'secondary';
  featured?: boolean;
};

export const riskHero = {
  eyebrow: 'ศูนย์การเรียนรู้การบริหารความเสี่ยง',
  description:
    'ทำความเข้าใจวิธีจัดการความเสี่ยงในการเทรด Forex ตั้งแต่การกำหนดขนาดการซื้อขาย การตั้ง Stop Loss การใช้ Leverage ไปจนถึงการควบคุม Drawdown เพื่อให้คุณวางแผนการเทรดได้อย่างรอบคอบมากขึ้น',
  primaryCta: 'เริ่มเรียนรู้การบริหารความเสี่ยง',
  secondaryCta: 'ดูบทความทั้งหมด',
  highlights: [
    { value: '4', label: 'ขั้นตอนพื้นฐาน', icon: 'ri-route-line', tone: 'primary' },
    { value: '6', label: 'หมวดบทความ', icon: 'ri-layout-grid-line', tone: 'accent' },
    { value: '30', label: 'บทความในคลัง', icon: 'ri-file-text-line', tone: 'secondary' },
  ],
  chart: {
    symbol: 'EUR/USD',
    timeframe: 'H1',
    entry: 62,
    stopLoss: 44,
    takeProfit: 88,
    caption: 'ภาพประกอบเพื่อการสอน ไม่ใช่ราคาเรียลไทม์หรือสัญญาณซื้อขาย',
  },
};

export type RiskLearningStage = {
  no: string;
  title: string;
  summary: string;
  points: string[];
  target: string;
  linkLabel: string;
};

export const riskLearningStages: RiskLearningStage[] = [
  {
    no: '01',
    title: 'ทำความเข้าใจเงินทุนและความเสี่ยง',
    summary:
      'เริ่มจากรู้ว่ามีเงินทุนเท่าไร ส่วนไหนยอมเสียได้ และหนึ่งออเดอร์ควรรับความเสี่ยงกี่เปอร์เซ็นต์ของพอร์ต',
    points: ['แยกเงินทุนเทรดออกจากเงินใช้จ่าย', 'กำหนดความเสี่ยงต่อออเดอร์เป็นเปอร์เซ็นต์', 'รู้ว่าขาดทุนได้กี่ครั้งก่อนต้องหยุด'],
    target: '#tutorial-basics',
    linkLabel: 'เริ่มจากพื้นฐานความเสี่ยง',
  },
  {
    no: '02',
    title: 'กำหนดขนาดการซื้อขาย',
    summary:
      'เมื่อรู้ความเสี่ยงที่รับได้แล้ว ขนาดสถานะจะถูกคำนวณย้อนกลับจากระยะ Stop Loss ไม่ใช่เลือกจากความมั่นใจ',
    points: ['ความเสี่ยงเป็นเงิน ÷ ระยะ Stop Loss', 'Stop Loss กว้างขึ้นต้องใช้ขนาดเล็กลง', 'ขนาดสถานะต้องคำนวณใหม่เมื่อพอร์ตเปลี่ยน'],
    target: '#tutorial-position',
    linkLabel: 'ไปที่หมวดขนาดการซื้อขาย',
  },
  {
    no: '03',
    title: 'เรียนรู้ Leverage และ Margin',
    summary:
      'เข้าใจว่า Leverage ทำให้เปิดสถานะใหญ่ขึ้นได้ด้วยเงินประกันน้อยลง และ Margin Call เกิดขึ้นเมื่อใด',
    points: ['Leverage ขยายทั้งกำไรและขาดทุน', 'Margin คือเงินประกันที่ถูกกันไว้', 'Stop Out คือการปิดออเดอร์อัตโนมัติ'],
    target: '#tutorial-leverage',
    linkLabel: 'ไปที่หมวด Leverage',
  },
  {
    no: '04',
    title: 'สร้างวินัยในการเทรด',
    summary:
      'วางแผนเทรดเป็นลายลักษณ์อักษร จำกัดการขาดทุนต่อเนื่อง และทบทวนผลการเทรดด้วยข้อมูลแทนความรู้สึก',
    points: ['เขียนแผนก่อนเปิดออเดอร์', 'กำหนดเพดานขาดทุนรายวัน', 'บันทึกและทบทวนทุกออเดอร์'],
    target: '#tutorial-discipline',
    linkLabel: 'ไปที่หมวดวินัยการเทรด',
  },
];

export const riskCategories: RiskCategory[] = [
  {
    id: 'basics',
    no: '01',
    title: 'พื้นฐานการบริหารความเสี่ยง',
    intro: 'ทำความเข้าใจว่าความเสี่ยงมาจากไหน และเหตุใดการจำกัดความเสี่ยงจึงมาก่อนการทำกำไรเสมอ',
    colSpan: 7,
    tone: 'primary',
    featured: true,
    articles: [
      { title: 'การบริหารความเสี่ยงในการเทรด Forex คืออะไร', slug: 'risk-management-basics' },
      { title: 'ความเสี่ยงในการเทรด Forex มีอะไรบ้าง', slug: 'forex-risks' },
      { title: 'ความเสี่ยงที่มือใหม่ควรรู้ก่อนเริ่มเทรด', slug: 'beginner-risks' },
      { title: 'ฝึกวางแผนความเสี่ยงบนบัญชีทดลองก่อนใช้เงินจริง', slug: 'forex-demo-account' },
      { title: 'ทำไมการเทรด Forex จึงขาดทุนได้', slug: 'why-forex-traders-lose' },
    ],
  },
  {
    id: 'position',
    no: '02',
    title: 'การจัดการขนาดการซื้อขาย',
    intro:
      'หัวใจของการบริหารความเสี่ยงคือการแปลงความเสี่ยงที่ยอมรับได้ให้เป็นขนาดสถานะที่วัดได้จริง ทุกครั้งที่ระยะ Stop Loss เปลี่ยน ขนาดสถานะก็ต้องเปลี่ยนตาม',
    colSpan: 5,
    tone: 'accent',
    featured: true,
    articles: [
      { title: 'Position Sizing คืออะไร', slug: 'position-sizing' },
      { title: 'วิธีคำนวณขนาดการซื้อขายจากเงินทุนและ Stop Loss', slug: 'how-to-calculate-lot' },
      { title: 'ความเสี่ยงต่อการเทรดหนึ่งครั้งคืออะไร', slug: 'risk-per-trade' },
      { title: 'Lot Size มีผลต่อความเสี่ยงอย่างไร', slug: 'what-is-lot' },
      { title: 'เปิดหลายออเดอร์พร้อมกันทำให้ความเสี่ยงสะสมเกินแผนได้อย่างไร', slug: 'forex-overtrading' },
    ],
  },
  {
    id: 'stop',
    no: '03',
    title: 'Stop Loss และ Take Profit',
    intro:
      'จุดตัดขาดทุนและจุดทำกำไรคือกรอบที่ทำให้ผลลัพธ์ของออเดอร์วัดได้ก่อนกดปุ่ม วางไว้จากโครงสร้างราคา ไม่ใช่จากความหวัง',
    colSpan: 4,
    tone: 'secondary',
    articles: [
      { title: 'Stop Loss คืออะไร และมีไว้เพื่ออะไร', slug: 'stop-loss-take-profit' },
      { title: 'Take Profit คืออะไร', slug: 'take-profit' },
      { title: 'Slippage ทำให้ Stop Loss คลาดจากที่ตั้งไว้อย่างไร', slug: 'what-is-slippage' },
      { title: 'Risk/Reward Ratio คืออะไร', slug: 'risk-reward-ratio' },
      { title: 'ทำไมการตั้ง Stop Loss จึงไม่รับประกันราคาปิดตามที่กำหนด', slug: 'stop-loss-not-guaranteed' },
    ],
  },
  {
    id: 'leverage',
    no: '04',
    title: 'ความเสี่ยงจาก Leverage และ Margin',
    intro: 'ตัวเลข Leverage ไม่ได้กำหนดความเสี่ยงด้วยตัวเอง แต่เป็นตัวกำหนดว่าพอร์ตจะรับแรงกระแทกได้แค่ไหน',
    colSpan: 4,
    tone: 'primary',
    articles: [
      { title: 'Leverage คืออะไร และเพิ่มความเสี่ยงอย่างไร', slug: 'what-is-leverage' },
      { title: 'Margin คืออะไร', slug: 'what-is-margin' },
      { title: 'Margin Call และ Stop Out แตกต่างกันอย่างไร', slug: 'margin-call-and-stop-out' },
      { title: 'ทำไมการใช้ Leverage สูงจึงทำให้ขาดทุนมากขึ้นได้', slug: 'high-leverage-risk' },
      { title: 'Margin Level คืออะไร และบอกอะไรเกี่ยวกับพอร์ต', slug: 'margin-level' },
    ],
  },
  {
    id: 'drawdown',
    no: '05',
    title: 'Drawdown และการขาดทุนต่อเนื่อง',
    intro: 'การลดลงของพอร์ตไม่ใช่แค่ตัวเลข แต่เป็นตัวกำหนดว่าต้องใช้เวลานานแค่ไหนกว่าจะกลับมาเท่าทุน',
    colSpan: 4,
    tone: 'accent',
    articles: [
      { title: 'Drawdown คืออะไร', slug: 'what-is-drawdown' },
      { title: 'วิธีคำนวณเปอร์เซ็นต์ Drawdown', slug: 'drawdown' },
      { title: 'การขาดทุนต่อเนื่องส่งผลต่อเงินทุนอย่างไร', slug: 'consecutive-losses' },
      { title: 'ทำไมการฟื้นตัวหลังขาดทุนจึงต้องใช้ผลตอบแทนที่สูงขึ้น', slug: 'recovery-after-loss' },
      { title: 'วิธีติดตามและทบทวนความเสี่ยงของบัญชีซื้อขาย', slug: 'forex-trading-journal' },
    ],
  },
  {
    id: 'discipline',
    no: '06',
    title: 'วินัยและจิตวิทยาการเทรด',
    intro:
      'แม้จะเข้าใจทุกสูตร แต่ถ้าอารมณ์เป็นคนตัดสินใจ ความเสี่ยงก็จะหลุดกรอบทันที บทความกลุ่มนี้ว่าด้วยพฤติกรรมที่ทำให้แผนยังอยู่',
    colSpan: 12,
    tone: 'secondary',
    articles: [
      { title: 'วิธีรับมือเมื่อการเทรดขาดทุน', slug: 'handling-trading-loss' },
      { title: 'ทำไมการแก้แค้นตลาดจึงเพิ่มความเสี่ยง', slug: 'revenge-trading' },
      { title: 'การเพิ่มขนาดการซื้อขายเพื่อเอาคืนมีความเสี่ยงอย่างไร', slug: 'increasing-size-after-loss' },
      { title: 'วิธีสร้างแผนการเทรดที่มีวินัย', slug: 'forex-trading-plan' },
      { title: 'วิธีทบทวนการซื้อขายโดยไม่มองเฉพาะกำไรหรือขาดทุน', slug: 'forex-trading-psychology' },
    ],
  },
];

export const riskCase = {
  account: '10,000 USD',
  riskPercent: '1%',
  riskAmount: '100 USD',
  steps: [
    {
      no: '01',
      title: 'เงินทุนในบัญชี',
      value: '10,000 USD',
      body: 'เริ่มจากยอดเงินในบัญชีซื้อขายจริง ไม่ใช่เงินที่วางแผนจะเติมเข้ามาในอนาคต',
    },
    {
      no: '02',
      title: 'ความเสี่ยงสมมติ',
      value: '1% ของเงินทุน',
      body: 'เลือกสัดส่วนความเสี่ยงคงที่ต่อออเดอร์ เพื่อให้ขาดทุนหลายครั้งติดกันแล้วยังควบคุมพอร์ตได้',
    },
    {
      no: '03',
      title: 'จำนวนเงินที่เสี่ยงตามแผน',
      value: '100 USD',
      body: 'ความเสี่ยงเป็นเงินคือเพดานขาดทุนของออเดอร์นี้ ไม่ใช่จำนวนเงินที่ต้องใช้เปิดสถานะ',
    },
    {
      no: '04',
      title: 'คำนวณขนาดจากระยะ Stop Loss',
      value: 'ยังไม่ใช่ Lot Size',
      body: 'นำ 100 USD หารด้วยระยะ Stop Loss และมูลค่าต่อจุดของคู่เงินนั้น จึงจะได้ขนาดสถานะที่เหมาะสม',
    },
  ],
  note:
    '1% เป็นเพียงตัวอย่างเพื่ออธิบายวิธีคำนวณ ไม่ใช่คำแนะนำสำหรับทุกคน ขนาดสถานะจริงยังขึ้นอยู่กับคู่เงิน จุดเข้า ระยะ Stop Loss ข้อมูลสัญญา สกุลเงินของบัญชี และต้นทุนการเทรด',
  links: [
    { title: 'วิธีคำนวณความเสี่ยงต่อการเทรด', slug: 'risk-per-trade' },
    { title: 'วิธีคำนวณขนาดการซื้อขาย', slug: 'how-to-calculate-lot' },
    { title: 'Risk/Reward Ratio คืออะไร', slug: 'risk-reward-ratio' },
  ],
};

export const riskDiagram = {
  entry: 62,
  stopLoss: 44,
  takeProfit: 88,
  legend: [
    { label: 'จุดเข้า (Entry)', body: 'ราคาที่แผนกำหนดให้เปิดสถานะ ใช้เป็นเส้นฐานของทั้งฝั่งเสี่ยงและฝั่งหวังผล', tone: 'foreground' as const },
    { label: 'Stop Loss', body: 'จุดที่ยอมรับว่าวิเคราะห์ผิด ระยะห่างจากจุดเข้าคือความเสี่ยงต่อหน่วยของออเดอร์นี้', tone: 'accent' as const },
    { label: 'Take Profit', body: 'เป้าหมายทำกำไรตามแผน ระยะห่างจากจุดเข้าคือผลตอบแทนที่คาดหวัง', tone: 'primary' as const },
    { label: 'ช่วงความเสี่ยงและผลตอบแทน', body: 'เมื่อเทียบสองช่วงนี้จะได้ Risk/Reward Ratio ซึ่งใช้ประเมินว่าออเดอร์คุ้มค่าหรือไม่', tone: 'secondary' as const },
  ],
  note:
    'แผนภาพนี้เป็นตัวอย่างเพื่อการเรียนรู้ ไม่ใช่การคาดการณ์ราคาและไม่ใช่สัญญาณซื้อขาย ในกรณีที่ตลาดเปิดช่องว่าง (Gap) หรือสภาพคล่องต่ำ ราคาที่ Stop Loss ถูกจับคู่จริงอาจต่างจากระดับที่ตั้งไว้',
};

export const riskMistakes = [
  {
    title: 'เปิดสถานะใหญ่เกินไปเมื่อเทียบกับเงินทุน',
    body: 'ขนาดสถานะที่ใหญ่ทำให้ราคาแกว่งเพียงเล็กน้อยก็กินเงินทุนหลายเปอร์เซ็นต์ ควรคำนวณจากความเสี่ยงที่รับได้ก่อนทุกครั้ง',
    to: '/high-leverage-risk',
  },
  {
    title: 'ไม่กำหนด Stop Loss หรือไม่เข้าใจข้อจำกัดของ Stop Loss',
    body: 'Stop Loss จำกัดความเสียหายตามแผน แต่ไม่ได้แปลว่าราคาจะปิดตรงจุดนั้นเสมอเมื่อตลาดกระโดดข้ามระดับ',
    to: '/stop-loss-not-guaranteed',
  },
  {
    title: 'ใช้ Leverage สูงโดยไม่เข้าใจความเสี่ยง',
    body: 'Leverage สูงทำให้วางเงินประกันน้อยลงและเปิดสถานะได้ใหญ่ขึ้น ซึ่งเพิ่มความเร็วของขาดทุนถ้าขนาดไม่ถูกควบคุม',
    to: '/what-is-leverage',
  },
  {
    title: 'เพิ่มขนาดการซื้อขายหลังจากขาดทุน',
    body: 'การเพิ่มขนาดเพื่อเอาคืนทำให้ความเสี่ยงต่อออเดอร์ไม่คงที่ และมักจบด้วยการขาดทุนที่ใหญ่กว่าครั้งก่อน',
    to: '/increasing-size-after-loss',
  },
  {
    title: 'เปิดหลายสถานะที่มีความเสี่ยงซ้ำซ้อนกัน',
    body: 'หลายออเดอร์ที่เดิมพันทิศทางเดียวกันถือเป็นความเสี่ยงก้อนเดียว ถ้าราคาสวนทางพร้อมกันพอร์ตจะรับแรงกระทบหลายเท่า',
    to: '/position-sizing',
  },
  {
    title: 'ไม่บันทึกและทบทวนผลการซื้อขาย',
    body: 'ถ้าไม่จดว่าเข้าเพราะอะไร ออกเพราะอะไร ก็แยกไม่ออกว่าผลลัพธ์มาจากแผนหรือโชค และแก้ไขได้ยาก',
    to: '/forex-trading-journal',
  },
];

export const riskRelatedTracks = [
  {
    title: 'Forex พื้นฐาน',
    description: 'ปูพื้นเรื่องคู่เงิน Pip Spread Leverage และ Margin ก่อนนำไปใช้คำนวณความเสี่ยง',
    path: '/forex-basics',
    icon: 'ri-book-open-line',
    meta: 'ปูพื้นฐานตลาด',
  },
  {
    title: 'วิเคราะห์ Forex',
    description: 'เรียนรู้การอ่านกราฟและกำหนดจุดเข้า เพื่อให้ระยะ Stop Loss อ้างอิงโครงสร้างราคาได้จริง',
    path: '/forex-analysis',
    icon: 'ri-bar-chart-2-line',
    meta: 'อ่านกราฟและโซนราคา',
  },
  {
    title: 'MT4 / MT5',
    description: 'ฝึกตั้ง Stop Loss, Take Profit และตรวจ Spread บนแพลตฟอร์มก่อนเปิดออเดอร์จริง',
    path: '/mt4-mt5',
    icon: 'ri-computer-line',
    meta: 'คู่มือแพลตฟอร์ม',
  },
];

/** Static teaching candles for the entry / stop / target illustrations. */
export const riskChartCandles = [
  { high: 58, low: 50, open: 52, close: 56 },
  { high: 60, low: 52, open: 56, close: 54 },
  { high: 59, low: 51, open: 54, close: 57 },
  { high: 63, low: 55, open: 57, close: 61 },
  { high: 66, low: 57, open: 61, close: 59 },
  { high: 65, low: 56, open: 59, close: 63 },
  { high: 69, low: 60, open: 63, close: 67 },
  { high: 72, low: 63, open: 67, close: 70 },
  { high: 71, low: 62, open: 70, close: 66 },
  { high: 69, low: 60, open: 66, close: 64 },
  { high: 70, low: 61, open: 64, close: 68 },
  { high: 75, low: 65, open: 68, close: 73 },
  { high: 79, low: 68, open: 73, close: 77 },
  { high: 83, low: 71, open: 77, close: 81 },
  { high: 88, low: 75, open: 81, close: 86 },
];